import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import { buscarVivo } from '../../db/registroVivo'
import type { ItemPedido, SubPedido } from '../../db/tipos'
import { formatarMoeda } from '../../lib/formatos'
import { itensForaDaRegra } from '../../pedidos/regraQuantidade'
import { sugerirPrevisaoEntrega } from '../../pedidos/entrega'
import {
  fecharPedido, verificarMinimos, type FechamentoSubPedido,
} from '../../pedidos/fechamento'
import { letrasPorMarca } from '../../pedidos/agrupamento'
import AvisoClienteInativo from '../../componentes/AvisoClienteInativo'
import { formasAtivasDaFornecedora, formaObrigatoria } from '../../representadas/formasDePagamento'
import { avisoCondicaoDaTabela } from '../../representadas/condicoesDaTabela'

// So usado para escrever em texto o que ja foi resolvido antes do carrinho
// (Task 5) -- o select de baixo, para quem ainda nao respondeu, tem as
// proprias opcoes com o mesmo rotulo.
const ROTULO_FRETE: Record<'CIF' | 'FOB' | 'REDESPACHO', string> = {
  CIF: 'CIF',
  FOB: 'FOB',
  REDESPACHO: 'Redespacho',
}

export default function FecharPedido() {
  const { id } = useParams()
  const navegar = useNavigate()
  const [fechamentos, setFechamentos] = useState<Record<string, FechamentoSubPedido>>({})
  const [confirmaMinimo, setConfirmaMinimo] = useState(false)
  // Caixinha PROPRIA, separada da do pedido minimo: sao perguntas diferentes
  // ("o pedido e grande o bastante?" x "as quantidades respeitam os degraus?")
  // e um visto so deixaria a pessoa liberar as duas tendo lido uma.
  const [confirmaQuantidade, setConfirmaQuantidade] = useState(false)

  const pedido = useLiveQuery(() => buscarVivo(banco.pedidos, id), [id])
  const cliente = useLiveQuery(
    () => (pedido ? banco.clientes.get(pedido.cliente_id) : undefined),
    [pedido?.cliente_id],
  )
  const subPedidos = useLiveQuery(
    () =>
      id
        ? banco.sub_pedidos
            .where('pedido_id')
            .equals(id)
            .filter((s) => s.apagado_em === null && s.status === 'rascunho')
            .sortBy('letra')
        : Promise.resolve<SubPedido[]>([]),
    [id],
  )
  const representadas = useLiveQuery(() => banco.representadas.toArray(), [])
  const condicoes = useLiveQuery(
    () => banco.condicoes_pagamento.filter((c) => c.apagado_em === null).sortBy('ordem'),
    [],
  )

  const tabelas = useLiveQuery(() => banco.tabelas_preco.toArray(), [])

  // ⚠ TRES consultas VIVAS novas. Ate aqui esta tela nao lia itens, nem precos,
  // nem produtos -- so sub-pedidos. Leitura unica num useEffect devolve vazio em
  // aparelho recem-aberto, e o aviso sairia em branco com teste verde: e o
  // defeito que ja mordeu 5 telas neste app.
  const itens = useLiveQuery(async () => {
    if (!subPedidos) return [] as ItemPedido[]
    const ids = subPedidos.map((sp) => sp.id)
    return banco.itens_pedido
      .where('sub_pedido_id').anyOf(ids)
      .filter((i) => i.apagado_em === null)
      .toArray()
  }, [subPedidos])
  const precos = useLiveQuery(
    () => banco.precos.filter((pr) => pr.apagado_em === null).toArray(),
    [],
  )
  // Os produtos entram so para escrever o NOME de cada item na lista de avisos:
  // "3 itens fora da quantidade" sem dizer quais nao ajuda ninguem.
  const produtos = useLiveQuery(() => banco.produtos.toArray(), [])

  const mapaRepresentadas = new Map(representadas?.map((r) => [r.id, r] as const))
  const mapaTabelas = new Map(tabelas?.map((t) => [t.id, t] as const))

  // pre-preenche a previsao de entrega sugerida de cada sub-pedido
  useEffect(() => {
    if (!subPedidos || !representadas || !cliente) return
    setFechamentos((atuais) => {
      const novos = { ...atuais }
      for (const subPedido of subPedidos) {
        if (novos[subPedido.id]) continue
        const representada = mapaRepresentadas.get(subPedido.representada_id)
        const previsao = sugerirPrevisaoEntrega(
          new Date(),
          representada?.prazo_medio_entrega_dias ?? null,
          cliente.dia_entrega,
        )
        // Ja resolvido antes do carrinho (representada com calcula_imposto,
        // modal da Task 5): mantem o que ja foi escolhido, nao pergunta de
        // novo. So os DOIS juntos contam -- o modal so confirma com os dois
        // preenchidos, e sub-pedido de fluxo normal nunca nasce com nenhum.
        if (subPedido.frete_tipo && subPedido.condicao_pagamento_id) {
          novos[subPedido.id] = {
            condicao_pagamento_id: subPedido.condicao_pagamento_id,
            frete_tipo: subPedido.frete_tipo,
            transportadora: null,
            previsao_entrega: previsao,
            forma_pagamento: null,
          }
          continue
        }
        novos[subPedido.id] = {
          condicao_pagamento_id: null,
          frete_tipo: 'CIF',
          transportadora: null,
          previsao_entrega: previsao,
          forma_pagamento: null,
        }
      }
      return novos
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subPedidos, representadas, cliente])

  if (!pedido || !subPedidos) return <p className="p-4 text-sm text-slate-500">Carregando…</p>

  const avisos = verificarMinimos(subPedidos, mapaRepresentadas)
  const foraDaRegra = itensForaDaRegra(itens ?? [], precos ?? [])
  const nomeProduto = (idProd: string) =>
    produtos?.find((pr) => pr.id === idProd)?.descricao ?? ''
  // Forma obrigatoria SO para fornecedora com forma ativa (escolha dela,
  // 27/09, "a"): as 39 de hoje nao tem nenhuma e nada pode travar na rua.
  const faltaForma = subPedidos.some((sp) =>
    formaObrigatoria(mapaRepresentadas.get(sp.representada_id)) && !fechamentos[sp.id]?.forma_pagamento)
  // As DUAS caixinhas sao exigidas em separado.
  const podeConfirmar =
    (avisos.length === 0 || confirmaMinimo) &&
    (foraDaRegra.length === 0 || confirmaQuantidade) &&
    !faltaForma

  function definir(subPedidoId: string, parcial: Partial<FechamentoSubPedido>) {
    setFechamentos((atuais) => ({
      ...atuais,
      [subPedidoId]: { ...atuais[subPedidoId], ...parcial },
    }))
  }

  async function aoConfirmar() {
    await fecharPedido(pedido!.id, fechamentos)
    navegar(`/pedidos/${pedido!.id}/enviar`, { replace: true })
  }

  const estiloCampo = 'w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm'

  return (
    <section className="p-4 space-y-4">
      <h2 className="text-lg font-bold text-slate-800">Fechar pedido {pedido.numero}</h2>

      {/* O ultimo momento util para ele saber com quem esta fechando: pode ter
          aberto o pedido de manha e so finalizar a tarde. Avisa, nao bloqueia. */}
      {cliente && <AvisoClienteInativo cliente={cliente} temPedido />}

      {(() => {
        // Uma marca pode ter varios sub-pedidos (um por tabela). Os cards ficam
        // agrupados sob a marca, com a letra DERIVADA — a mesma do PDF.
        const letras = letrasPorMarca(subPedidos)
        // Map<string, SubPedido[]> explicito, NAO `typeof subPedidos`: o tipo
        // declarado do useLiveQuery inclui undefined, e `typeof` devolve o
        // declarado, nao o estreitado pelo early return acima.
        const porMarca = new Map<string, SubPedido[]>()
        for (const subPedido of subPedidos) {
          const lista = porMarca.get(subPedido.representada_id)
          if (lista) lista.push(subPedido)
          else porMarca.set(subPedido.representada_id, [subPedido])
        }
        return [...porMarca.entries()].map(([representadaId, subsDaMarca]) => (
          <div key={representadaId} className="space-y-2">
            <h3 className="font-medium text-slate-800 px-1">
              {letras.get(representadaId)} — {mapaRepresentadas.get(representadaId)?.nome}
            </h3>
            {subsDaMarca.map((subPedido) => {
              const condicoesDaRepresentada =
                condicoes?.filter((c) => c.representada_id === representadaId) ?? []
              const fechamento = fechamentos[subPedido.id]
              if (!fechamento) return null
              const nomeTabela = subPedido.tabela_preco_id
                ? mapaTabelas.get(subPedido.tabela_preco_id)?.nome ?? 'Tabela'
                : 'Sem tabela'
              const representada = mapaRepresentadas.get(representadaId)
              const formas = formasAtivasDaFornecedora(representada)
              const aviso = avisoCondicaoDaTabela(
                subPedido.tabela_preco_id ? mapaTabelas.get(subPedido.tabela_preco_id) : undefined,
                fechamento.condicao_pagamento_id, condicoesDaRepresentada,
              )
              return (
                <div key={subPedido.id} className="bg-white rounded-xl shadow-sm p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-slate-600">{nomeTabela}</h4>
                    <span className="text-sm font-bold">
                      {formatarMoeda(subPedido.valor_total)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {subPedido.frete_tipo && subPedido.condicao_pagamento_id ? (
                      // Ja respondido no modal ANTES do carrinho (Task 5,
                      // representada com calcula_imposto): so mostra o que foi
                      // escolhido, nao deixa editar aqui -- o motor de imposto
                      // ja calculou os itens em cima disto, e trocar frete ou
                      // condicao no fechamento desalinharia o preco gravado do
                      // que aparece na tela.
                      <p className="col-span-2 text-xs text-slate-600 bg-slate-50 rounded-lg px-2 py-1.5">
                        Frete: {ROTULO_FRETE[subPedido.frete_tipo]} · Condição:{' '}
                        {condicoesDaRepresentada.find(
                          (c) => c.id === subPedido.condicao_pagamento_id,
                        )?.descricao ?? subPedido.condicao_pagamento_id}
                      </p>
                    ) : (
                      <>
                        <div>
                          <label className="block text-xs text-slate-500 mb-0.5">
                            Condição de pagamento
                          </label>
                          <select
                            value={fechamento.condicao_pagamento_id ?? ''}
                            onChange={(e) =>
                              definir(subPedido.id, {
                                condicao_pagamento_id: e.target.value || null,
                              })
                            }
                            className={estiloCampo}
                          >
                            <option value="">—</option>
                            {condicoesDaRepresentada.map((c) => (
                              <option key={c.id} value={c.id}>{c.descricao}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-0.5">Frete</label>
                          <select
                            value={fechamento.frete_tipo ?? ''}
                            onChange={(e) => {
                              const valor = e.target.value as 'CIF' | 'FOB' | 'REDESPACHO' | ''
                              definir(subPedido.id, { frete_tipo: valor || null })
                            }}
                            className={estiloCampo}
                          >
                            <option value="CIF">CIF (pago pela fábrica)</option>
                            <option value="FOB">FOB (pago pelo cliente)</option>
                            <option value="REDESPACHO">Redespacho</option>
                          </select>
                        </div>
                      </>
                    )}
                    <div>
                      <label className="block text-xs text-slate-500 mb-0.5">Transportadora</label>
                      <input
                        list={`transp-${subPedido.id}`}
                        value={fechamento.transportadora ?? ''}
                        onChange={(e) =>
                          definir(subPedido.id, { transportadora: e.target.value || null })
                        }
                        className={estiloCampo}
                      />
                      <datalist id={`transp-${subPedido.id}`}>
                        {mapaRepresentadas
                          .get(representadaId)
                          ?.transportadoras_usuais.map((t) => <option key={t} value={t} />)}
                      </datalist>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-0.5">
                        Previsão de entrega
                      </label>
                      <input
                        type="date"
                        value={fechamento.previsao_entrega ?? ''}
                        onChange={(e) =>
                          definir(subPedido.id, { previsao_entrega: e.target.value || null })
                        }
                        className={estiloCampo}
                      />
                    </div>
                  </div>
                  {formas.length > 0 && (
                    <div>
                      <label className="block text-xs text-slate-500 mb-0.5">Forma de pagamento</label>
                      <select value={fechamento.forma_pagamento ?? ''} className={estiloCampo}
                        onChange={(e) => definir(subPedido.id, { forma_pagamento: e.target.value || null })}>
                        <option value="">Selecione…</option>
                        {formas.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                  )}
                  {/* Aviso, nunca bloqueio (escolha dela, 27/09). */}
                  {aviso && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-2 py-1.5">⚠ {aviso}</p>}
                </div>
              )
            })}
          </div>
        ))
      })()}

      {avisos.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 space-y-2">
          <h4 className="text-sm font-medium text-amber-800">Pedido mínimo não atingido</h4>
          {avisos.map((aviso) => (
            <p key={aviso.representadaId} className="text-xs text-amber-700">
              {aviso.representadaNome}: faltam {formatarMoeda(aviso.faltam)}
            </p>
          ))}
          <label className="flex items-center gap-2 text-sm text-amber-800">
            <input
              type="checkbox"
              checked={confirmaMinimo}
              onChange={(e) => setConfirmaMinimo(e.target.checked)}
            />
            Enviar mesmo assim (a fábrica pode recusar)
          </label>
        </div>
      )}

      {foraDaRegra.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 space-y-2">
          <h4 className="text-sm font-medium text-amber-800">
            {foraDaRegra.length === 1
              ? '1 item fora da quantidade da tabela'
              : `${foraDaRegra.length} itens fora da quantidade da tabela`}
          </h4>
          {foraDaRegra.map((f) => (
            <p key={f.itemId} className="text-xs text-amber-700">
              {nomeProduto(f.produtoId)}: {f.quantidade} un
              {f.tipo === 'abaixo' ? ` (mín. ${f.limite})` : ` (máx. ${f.limite})`}
            </p>
          ))}
          <label className="flex items-center gap-2 text-sm text-amber-800">
            <input
              type="checkbox"
              checked={confirmaQuantidade}
              onChange={(e) => setConfirmaQuantidade(e.target.checked)}
            />
            Enviar mesmo assim (a fábrica pode recusar)
          </label>
        </div>
      )}

      {faltaForma && <p className="text-xs text-slate-600">Escolha a forma de pagamento para confirmar.</p>}

      <button
        onClick={() => void aoConfirmar()}
        disabled={!podeConfirmar}
        className="w-full bg-teal-700 text-white rounded-lg py-3 font-medium disabled:opacity-50"
      >
        Confirmar e gerar PDFs
      </button>
    </section>
  )
}
